// ── Marketing (2026-09-24) ───────────────────────────────────────────
// Section 18 of layout-3 turnovers (src/pages/turnover/MarketingKickoffPanel.jsx).
// Marketing (the section's default owner), the salesperson and the PM work
// through it together; four parts each name a lead and are signed off, and
// the section — like every other — must be complete (or N/A) before the
// package is. The turnover meeting is the kickoff meeting. Client
// confidentiality lives on the Cover Sheet (Turnovers.PublicReference …).
//
// Facts the turnover already holds are READ, never re-entered: market, SF,
// the HubSpot deal description and Closed Won date, and the §9 milestones.
// Everything else is stored as one JSON object in TurnoverMarketingKickoff
// (db/04). Keys below are stored — never rename one; add new ones instead.
// Pure: no React, no I/O.

export const YES_NO_TBD = [
  { key: "yes", label: "Yes" },
  { key: "no", label: "No" },
  { key: "tbd", label: "TBD" },
];

export const PUBLIC_REFERENCE = [
  { key: "yes", label: "Yes — no restrictions" },
  { key: "limited", label: "Limited — client approval required" },
  { key: "no", label: "No — confidential" },
];

export const SIGNAGE = {
  branding: {
    label: "Site branding",
    items: [
      { key: "B1", label: "B1 – Site Branding Combined (3'×5' / 6'×10')" },
      { key: "B2", label: "B2 – Site Branding Combined (alternate layout)" },
      { key: "B3", label: "B3 – CleanSpace Only (2'×5' / 7'×16')" },
      { key: "B4", label: "B4 – Allied Only (2'×5' / 7'×16')" },
      { key: "B4S", label: "B4 – Site Fence Scrim (5'8\"×50')" },
    ],
  },
  construction: {
    label: "Construction & safety signage",
    items: [
      { key: "C1", label: "C1 – Safety Notice 24\"×36\"", required: true },
      { key: "C2", label: "C2 – Construction Entrance 24\"×48\"" },
      { key: "C3", label: "C3 – Construction Traffic (Left) 12\"×18\"" },
      { key: "C4", label: "C4 – Construction Traffic (Right) 12\"×18\"" },
      { key: "C5", label: "C5 – Field Office 12\"×18\"" },
      { key: "C6", label: "C6 – Field Office (Left) 12\"×18\"" },
      { key: "C7", label: "C7 – Field Office (Right) 12\"×18\"" },
      { key: "C8", label: "C8 – No Food or Drink 12\"×18\"" },
      { key: "C9", label: "C9 – Hard Hats Required 12\"×18\"" },
      { key: "C10", label: "C10 – Safety Glasses Required 12\"×18\"" },
      { key: "C11", label: "C11 – Laser in Use 12\"×18\"" },
      { key: "C12", label: "C12 – Fall Protection Required 12\"×18\"" },
      { key: "C13", label: "C13 – Primary Muster Location 24\"×36\"" },
      { key: "C14", label: "C14 – Secondary Muster Location 24\"×36\"" },
      { key: "C15", label: "C15 – PM / Foreman Contacts 12\"×18\"" },
      { key: "X1", label: "X1 – Custom Sign" },
    ],
  },
  marketing: {
    label: "Marketing signage",
    items: [{ key: "M1", label: "M1 – Marketing Poster (Design Build)" }],
  },
};

export const CONTENT_OPPORTUNITIES = [
  { key: "case_study", label: "Case study" },
  { key: "construction_photo", label: "Construction photography" },
  { key: "completion_photo", label: "Completion photography" },
  { key: "drone", label: "Drone / aerial footage" },
  { key: "video", label: "Video / testimonial" },
  { key: "award", label: "Award submission" },
  { key: "press_release", label: "Press release" },
  { key: "social", label: "Social media posts" },
  { key: "topping_out", label: "Topping out event" },
  { key: "grand_opening", label: "Grand opening event" },
];

export const MARKETING_TEAM_GROUP = "marketing_team";

export function emptyKickoff() {
  return {
    industry: "", estimatedStart: "", estimatedCompletion: "", keyMilestones: "",
    publicReference: "", confidentialityNotes: "", photographyRestrictions: "",
    signageRequired: "", signs: { C1: true }, decalsRequired: "", decalType: "", estimatedWorkers: "", decalsNumbered: "",
    signageNotes: "",
    projectDescription: "", notableFeatures: "", imageryAvailable: "",
    opportunities: {}, photographyTiming: "", photographyBudgetApproved: "", contentNotes: "",
    meetingDate: "", meetingLedBy: "", attendees: "", actionItems: "",
    // Layout 3 (§18): each part's sign-off { key: { by, at } }, §9 milestones
    // ticked as photo / content opportunities { TurnoverScheduleID: true },
    // and Marketing's follow-ups from the turnover meeting.
    partsDone: {}, photoMilestones: {}, followUps: "",
  };
}

// Stored answers over the empty shape; C1 (safety notice) is always required.
export function normalizeKickoff(data) {
  let d = data;
  if (typeof d === "string") { try { d = JSON.parse(d); } catch { d = null; } }
  const base = emptyKickoff();
  const out = { ...base, ...(d || {}) };
  out.signs = { ...(d?.signs || {}), C1: true };
  out.opportunities = { ...(d?.opportunities || {}) };
  out.partsDone = { ...(d?.partsDone || {}) };
  out.photoMilestones = { ...(d?.photoMilestones || {}) };
  return out;
}

const toDate = (v) => {
  if (!v) return null;
  const d = new Date(String(v).length <= 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? null : d;
};
export const isoDate = (v) => { const d = toDate(v); return d ? d.toISOString().slice(0, 10) : ""; };

// Estimated start / completion from the §9 milestones (TurnoverSchedule rows):
// start = Site Kick-off, else Onsite Mobilization, else the earliest dated
// milestone; completion = Substantial Completion, else Final Completion, else
// the latest dated milestone. Returns { date, from } or null.
const START_LABELS = ["Site Kick-off", "Onsite Mobilization"];
const FINISH_LABELS = ["Substantial Completion", "Final Completion / Punch List Complete"];
export function milestoneDates(schedule = []) {
  const dated = (schedule || []).filter(m => toDate(m.MilestoneDate))
    .map(m => ({ id: m.TurnoverScheduleID, label: m.MilestoneLabel, date: isoDate(m.MilestoneDate) }))
    .sort((a, b) => a.date.localeCompare(b.date));
  const pick = (labels, fallback) => {
    for (const l of labels) { const hit = dated.find(m => m.label === l); if (hit) return { date: hit.date, from: hit.label }; }
    return fallback ? { date: fallback.date, from: fallback.label } : null;
  };
  return {
    start: pick(START_LABELS, dated[0]),
    completion: pick(FINISH_LABELS, dated[dated.length - 1]),
    milestones: dated,
  };
}

// The facts shown in "Project details" — all read from the turnover.
export function projectFacts({ turnover = {}, hsCompanies = [], hsLineItems = [], closeDate = null }) {
  const owner = (hsCompanies || []).find(c => String(c.label || "").trim().toLowerCase() === "owner");
  const address = owner ? [owner.address, [owner.city, owner.state, owner.zip].filter(Boolean).join(", ")].filter(Boolean).join(", ") : "";
  const releases = (hsLineItems || []).reduce((s, li) => s + (Number(String(li.amount ?? "").replace(/[$,\s]/g, "")) || 0), 0);
  const value = releases > 0 ? releases : Number(String(turnover.ContractValue || turnover.DealAmount || "").replace(/[$,\s]/g, "")) || null;
  return {
    projectName: turnover.ProjectName || "",
    jobNumber: turnover.JobNumber || "",
    client: turnover.ClientName || "",
    location: address || turnover.CityState || "",
    market: turnover.Market || "",
    projectType: turnover.ProjectType || "",
    contractValue: value,
    squareFeet: Number(turnover.SquareFeet) || null,
    awardDate: isoDate(closeDate),
    description: turnover.DealDescription || "",
  };
}

const sameEmail = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
const sameName = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

// Marketing (the Turnover Configuration "Marketing Team" list), the
// salesperson and the turnover's PM (stored by name) may edit; admins too.
export function canEditKickoff(turnover, user, configContacts = [], isAdmin = false) {
  if (isAdmin) return true;
  if (!user) return false;
  if (sameEmail(turnover?.SalespersonEmail, user.email)) return true;
  if (sameName(turnover?.ProjectManager, user.name)) return true;
  return (configContacts || []).some(c => c.GroupKey === MARKETING_TEAM_GROUP && sameEmail(c.Email, user.email));
}

// ── §18 Marketing (layout 3) ─────────────────────────────────────────
// Four parts, each with a suggested lead and its own "done" sign-off. The
// section completes when every part is signed off (Mark N/A still works).
export const KICKOFF_PARTS = [
  { key: "signage", label: "Signage & hard hat decals", lead: "Project Manager" },
  { key: "content", label: "Content & marketing opportunities", lead: "Marketing" },
  { key: "sheet", label: "Project sheet", lead: "Marketing" },
  { key: "milestones", label: "Milestones & photo opportunities", lead: "Project Manager" },
];

const filled = (v) => String(v ?? "").trim() !== "";

// The project description, pre-filled from the HubSpot deal until someone edits it.
export const effectiveDescription = (k, turnover) =>
  filled(k.projectDescription) ? k.projectDescription : (turnover?.DealDescription || "");

// Start / completion: §9 first, else what was typed here.
export function kickoffDates(k, schedule) {
  const ms = milestoneDates(schedule);
  return {
    start: ms.start?.date || k.estimatedStart || "", startFrom: ms.start?.from || "",
    completion: ms.completion?.date || k.estimatedCompletion || "", completionFrom: ms.completion?.from || "",
    milestones: ms.milestones,
  };
}

// What a part still needs before it can be signed off.
export function partMissing(key, data, { schedule = [], turnover = null } = {}) {
  const k = normalizeKickoff(data);
  const out = [];
  if (key === "signage") {
    if (!filled(k.signageRequired)) out.push("Answer: signage required?");
    if (!filled(k.decalsRequired)) out.push("Answer: hard hat decals required?");
  } else if (key === "sheet") {
    if (!filled(effectiveDescription(k, turnover))) out.push("Project description");
    if (!filled(k.imageryAvailable)) out.push("Answer: existing project imagery?");
  } else if (key === "milestones") {
    const d = kickoffDates(k, schedule);
    if (!d.start) out.push("Estimated start date (§9 or here)");
    if (!d.completion) out.push("Estimated completion date (§9 or here)");
  }
  return out;
}

// Everything between §18 and ✓ Complete, in the Mark Complete blocker style.
export function kickoffMissing(data, ctx = {}) {
  const k = normalizeKickoff(data);
  const out = [];
  for (const p of KICKOFF_PARTS) {
    const need = partMissing(p.key, k, ctx);
    if (need.length) out.push(`${p.label}: ${need.join("; ")}`);
    else if (!k.partsDone[p.key]) out.push(`${p.label}: mark it done (${p.lead})`);
  }
  return out;
}

export const PUBLIC_REFERENCE_LABEL = Object.fromEntries(PUBLIC_REFERENCE.map(o => [o.key, o.label]));

// How far along the kickoff is: parts signed off.
export function kickoffProgress(d) {
  const k = normalizeKickoff(d);
  const parts = KICKOFF_PARTS.map(p => ({ ...p, done: !!k.partsDone[p.key] }));
  return { parts, done: parts.filter(p => p.done).length, total: parts.length };
}

export const kickoffPayload = (turnoverId, data, user) =>
  ({ turnover_id: turnoverId, data: normalizeKickoff(data), by: user?.name || "" });
